import React, { memo, useCallback } from 'react';
import cl from 'classnames';

import { IconsEnum } from '../../../../assets/icons/types';

import { Icon } from '../../../../helpers/Icon';

export interface ComposerDockProductItemProduct {
  id: string;
  name: string;
  image?: { file: string } | null;
  brand?: { name: string; localizedName?: string } | null;
  previewModel?: { file: string } | null;
}

interface ComposerDockProductItemProps {
  product: ComposerDockProductItemProduct;
  onAddToScene: (product: ComposerDockProductItemProduct) => void;
  onDragStart?: (
    e: React.DragEvent,
    product: ComposerDockProductItemProduct
  ) => void;
  onDragEnd?: (e: React.DragEvent) => void;
}

function ComposerDockProductItem({
  product,
  onAddToScene,
  onDragStart,
  onDragEnd
}: ComposerDockProductItemProps) {
  const hasModel = !!product.previewModel?.file;

  const handleClick = useCallback(() => {
    if (!hasModel) return;
    onAddToScene(product);
  }, [hasModel, onAddToScene, product]);

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      if (!hasModel) {
        e.preventDefault();
        return;
      }
      onDragStart?.(e, product);
    },
    [hasModel, onDragStart, product]
  );

  const handleDragEnd = useCallback(
    (e: React.DragEvent) => {
      onDragEnd?.(e);
    },
    [onDragEnd]
  );

  const brandName = product.brand?.localizedName || product.brand?.name;

  return (
    <div
      draggable={hasModel}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      aria-disabled={!hasModel}
      data-compose-product
      className={cl(
        'group',
        hasModel
          ? 'cursor-grab active:cursor-grabbing'
          : 'cursor-not-allowed opacity-40'
      )}
    >
      <div className="aspect-square rounded bg-gray-100 dark:bg-gray-850 relative overflow-hidden ring-1 ring-transparent group-hover:ring-blue-400 group-active:ring-blue-500 transition-shadow">
        {product.image?.file && (
          <img
            className="object-cover h-full w-full rounded pointer-events-none"
            src={product.image.file}
            alt={product.name}
            loading="lazy"
            decoding="async"
            width={256}
            height={256}
          />
        )}

        {hasModel && (
          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900/70 text-white rounded p-0.5">
            <Icon icon={IconsEnum.PLUS} className="h-3.5 w-3.5" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-0.5 py-1">
        {brandName && (
          <div className="text-2xs line-clamp-1 text-gray-500">{brandName}</div>
        )}
        <div className="text-2xs line-clamp-2">{product.name}</div>
      </div>
    </div>
  );
}

export default memo<ComposerDockProductItemProps>(ComposerDockProductItem);
